import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Input } from './Input';
import { CalendarPicker } from './CalendarPicker';
import { cn } from '@/shared/lib/utils';

interface DatePickerWithCalendarProps {
  id?: string;
  label?: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  min?: string | undefined;
  max?: string | undefined;
  required?: boolean;
  disabled?: boolean;
  error?: string | undefined;
  className?: string;
  'data-testid'?: string;
  formatDate?: (dateStr: string) => string;
}

export const DatePickerWithCalendar: React.FC<DatePickerWithCalendarProps> = ({
  id,
  label,
  value,
  onChange,
  min,
  max,
  required = false,
  disabled = false,
  error,
  className = '',
  'data-testid': dataTestId,
  formatDate,
}) => {
  const { t } = useTranslation('translation');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const calendarPortalRef = useRef<HTMLDivElement>(null);
  const [calendarStyle, setCalendarStyle] = useState<React.CSSProperties>({});

  // Convert string date ("YYYY-MM-DD") to a LOCAL-tz Date.
  //
  // `new Date("YYYY-MM-DD")` parses as UTC midnight per the ECMAScript spec,
  // which then displays one day earlier in any timezone west of UTC (e.g.
  // EDT/EST renders Apr 24 UTC midnight as Apr 23 19:00 local). The fix is
  // to parse the components and construct a Date in local time. Closes the
  // off-by-one calendar bug observed on QA #224 / #227 follow-up.
  const stringToDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    const isoDate = dateStr.split('T')[0];
    const parts = isoDate.split('-');
    if (parts.length !== 3) return null;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
    const date = new Date(year, month - 1, day);
    return isNaN(date.getTime()) ? null : date;
  };

  // Convert Date object to string (YYYY-MM-DD)
  const dateToString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const selectedDate = stringToDate(value);
  const minDate = min ? stringToDate(min) || undefined : undefined;
  const maxDate = max ? stringToDate(max) || undefined : undefined;

  /** Recompute the calendar's fixed position from the trigger element's rect. */
  const updateCalendarPosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const calendarHeight = 320; // approximate max height of CalendarPicker (w-[280px])
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < calendarHeight && rect.top > calendarHeight;

    setCalendarStyle({
      position: 'fixed',
      zIndex: 9999,
      width: rect.width,
      left: rect.left,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  }, []);

  // Handle click outside (including the portal) or Escape to close calendar
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = containerRef.current?.contains(target);
      const inPortal = calendarPortalRef.current?.contains(target);
      if (!inTrigger && !inPortal) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    const handleScrollOrResize = () => {
      if (isOpen) updateCalendarPosition();
    };

    if (isOpen) {
      updateCalendarPosition();
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
      window.addEventListener('scroll', handleScrollOrResize, true);
      window.addEventListener('resize', handleScrollOrResize);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen, updateCalendarPosition]);

  const handleDateSelect = (date: Date): void => {
    onChange(dateToString(date));
    setIsOpen(false);
  };

  const handleTodayClick = (): void => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (minDate && today < minDate) return;
    if (maxDate && today > maxDate) return;
    onChange(dateToString(today));
    setIsOpen(false);
  };

  const handleClearClick = (): void => {
    onChange('');
    setIsOpen(false);
  };

  const isTodayDisabled = (): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (minDate && today < minDate) return true;
    if (maxDate && today > maxDate) return true;
    return false;
  };

  const handleInputClick = (): void => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const formatDisplayDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const date = stringToDate(dateStr);
    if (!date) return '';

    // Match the portal-wide display format: "13 May 2026" (day month year, no comma)
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date);
  };

  return (
    <div className={cn('relative', className)} ref={containerRef}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-text mb-1" title={typeof label === 'string' ? label : undefined}>
          {label}
          {required && <span className="text-error ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <Input
          id={id}
          type="text"
          value={formatDate ? formatDate(value) : formatDisplayDate(value)}
          readOnly
          disabled={disabled}
          onClick={handleInputClick}
          className={cn(
            'cursor-pointer pr-10',
            error ? 'border-error' : ''
          )}
          data-testid={dataTestId || 'date-picker-input'}
        />
        <button
          type="button"
          className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-auto bg-transparent border-0 p-0 cursor-pointer"
          onClick={handleInputClick}
          disabled={disabled}
          aria-label={t('shared.datePicker.openCalendar')}
          tabIndex={0}
        >
          <svg
            className={cn(
              'w-5 h-5 transition-colors',
              disabled ? 'text-text-disabled' : 'text-text-muted'
            )}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </button>
      </div>
      {error && <p className="text-error text-sm mt-1">{error}</p>}

      {/* Calendar Dropdown — rendered via Portal to escape sidebar overflow/clip */}
      {isOpen && !disabled && createPortal(
        <div ref={calendarPortalRef} style={calendarStyle}>
          <CalendarPicker
            value={selectedDate}
            onChange={handleDateSelect}
            {...(minDate !== undefined && { minDate })}
            {...(maxDate !== undefined && { maxDate })}
            data-testid={`${dataTestId || 'date-picker'}-calendar`}
          />
          {/* ── Clear / Today footer ─────────────────────────────────────── */}
          <div className="bg-surface rounded-b-lg shadow-lg border-t border-border px-3 py-2 flex items-center justify-between w-[280px]">
            <button
              type="button"
              onClick={handleClearClick}
              disabled={!value}
              className={cn(
                'text-xs font-medium px-2 py-1 rounded-md transition-colors',
                'text-text-muted hover:text-text hover:bg-surface-alt',
                'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-text-muted',
                'focus:outline-none focus:ring-1 focus:ring-primary focus:ring-offset-1',
              )}
              data-testid={`${dataTestId || 'date-picker'}-clear`}
              aria-label={t('shared.datePicker.clearDate')}
            >
              {t('global.clear')}
            </button>
            <button
              type="button"
              onClick={handleTodayClick}
              disabled={isTodayDisabled()}
              className={cn(
                'text-xs font-medium px-2 py-1 rounded-md transition-colors',
                'text-primary hover:bg-primary/10',
                'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent',
                'focus:outline-none focus:ring-1 focus:ring-primary focus:ring-offset-1',
              )}
              data-testid={`${dataTestId || 'date-picker'}-today`}
              aria-label={t('shared.datePicker.selectToday')}
            >
              {t('global.today')}
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};